// ---------------------------------------------------------------------------
// Social Media Hub Academy — 15-slide presentation.
// All 21 course sections are condensed into 15 teaching slides + a review
// step (slide 16) that hands off to the certification assessment.
// Every slide ships with two "what you'll actually see" screenshots:
// an iPhone (mobile) style and a desktop browser style.
// ---------------------------------------------------------------------------

import { COURSE_SECTIONS, TOTAL_MINUTES } from './trainingAcademy';

const CDN = 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_';

export interface PresentationSlide {
  id: string;
  num: number;
  kicker: string;
  title: string;
  lead: string;
  points: { title: string; desc: string }[];
  tip?: { title: string; text: string };
  /** One-line recap shown on the review slide + training PDF. */
  recap: string;
  /** Investor / client framing used in the pitch-deck PDF. */
  pitch: string;
  /** Original Academy section ids this slide covers (quiz review links). */
  sectionIds: string[];
  images: { mobile: string; desktop: string };
  imageAlt: string;
}

export const PRESENTATION_SLIDES: PresentationSlide[] = [
  {
    id: 'pres-1',
    num: 1,
    kicker: 'Welcome · The Hub at a glance',
    title: 'Your Social Media Command Center',
    lead: 'The Social Media Hub gathers every step of professional social publishing — writing, imagery, approval, scheduling and analytics — into one dark-gold workspace. The dashboard is your daily home base.',
    points: [
      { title: 'One hub, every job', desc: 'Draft, approve, schedule, publish and measure without leaving the platform.' },
      { title: 'Dashboard stat cards', desc: 'Posts scheduled, items pending approval and engagement rate greet you on login.' },
      { title: 'Weekly strip', desc: 'A seven-day calendar strip previews exactly what goes live and when.' },
      { title: 'Activity feed', desc: 'Recent edits, approvals and publishes so nothing slips past you.' },
    ],
    tip: { title: 'Start here every day', text: 'A 60-second dashboard scan each morning tells you what needs approval, what publishes today and how yesterday performed.' },
    recap: 'The dashboard is your daily home base: stats, the weekly strip and the activity feed.',
    pitch: 'A single command center replaces four or five disconnected tools — teams see schedule, approvals and performance the moment they log in.',
    sectionIds: ['intro', 'dashboard'],
    images: { mobile: `${CDN}1783415475445_9f592cf9.jpg`, desktop: `${CDN}1783415090241_21bcb035.jpg` },
    imageAlt: 'Social Media Hub dashboard with stat cards, weekly calendar strip and activity feed',
  },
  {
    id: 'pres-2',
    num: 2,
    kicker: 'Getting around',
    title: 'Navigation: Everything Two Clicks Away',
    lead: 'A persistent navigation menu keeps the whole Hub within reach. On desktop it lives in the sidebar; on mobile it slides in as a drawer — same items, same order, everywhere.',
    points: [
      { title: 'Seven core areas', desc: 'Dashboard, Content, Calendar, Approvals, Analytics, Brand Studio and Settings.' },
      { title: 'Gold active state', desc: 'The highlighted item always shows where you are — you can never get lost.' },
      { title: 'Breadcrumbs', desc: 'Deep pages show a breadcrumb trail so you can step back one level at a time.' },
      { title: 'Consistent on mobile', desc: 'The drawer mirrors the desktop sidebar so training transfers 1:1 between devices.' },
    ],
    recap: 'Seven nav areas with a gold active state; the mobile drawer mirrors the desktop sidebar.',
    pitch: 'Zero-training navigation: the same seven-item structure on phone and desktop means new team members are productive on day one.',
    sectionIds: ['navigation'],
    images: { mobile: `${CDN}1783415502639_9267bd9d.png`, desktop: `${CDN}1783415113827_63e9087f.png` },
    imageAlt: 'Hub navigation menu with Dashboard, Content, Calendar, Approvals, Analytics, Brand Studio and Settings',
  },
  {
    id: 'pres-3',
    num: 3,
    kicker: 'Creating',
    title: 'Content Creation: The Composer',
    lead: 'Every post begins in the composer — caption editor, hashtag chips, media upload and platform selector on one screen. Drafts save automatically as you type.',
    points: [
      { title: 'Caption editor', desc: 'Write long-form captions with live character counts per platform.' },
      { title: 'Hashtag chips', desc: 'Add, reorder and reuse hashtag sets; the Hub remembers your best performers.' },
      { title: 'Media panel', desc: 'Drag-and-drop images or pull straight from the media library.' },
      { title: 'Platform targeting', desc: 'Pick the destination account before you write, so previews match reality.' },
    ],
    tip: { title: 'Draft first, polish later', text: 'Nothing goes live from the composer — everything flows into the approval workflow, so draft freely.' },
    recap: 'The composer combines caption, hashtags, media and platform targeting; drafts auto-save.',
    pitch: 'A frictionless composer means the team captures ideas instantly — while guardrails guarantee nothing publishes without review.',
    sectionIds: ['content-creation'],
    images: { mobile: `${CDN}1783415522570_55b4207f.jpg`, desktop: `${CDN}1783415136529_622c572e.png` },
    imageAlt: 'Post composer with caption editor, hashtag chips and coastal image preview',
  },
  {
    id: 'pres-4',
    num: 4,
    kicker: 'AI assistance · Writing',
    title: 'AI Writing Tools',
    lead: 'The AI writing assistant drafts captions in your brand voice. Give it a topic, choose a tone, and it returns ready-to-edit suggestions — singly or as a full week in bulk.',
    points: [
      { title: 'Tone selector', desc: 'Luxury, Playful or Editorial — the AI adapts phrasing to the chip you pick.' },
      { title: 'Three suggestions per run', desc: 'Compare angles side by side and copy the one that fits.' },
      { title: 'Brand-voice aware', desc: 'Suggestions are conditioned on your saved brand voice profile.' },
      { title: 'Bulk generation', desc: 'Generate a full week of captions in one pass, then refine per post.' },
    ],
    tip: { title: 'AI drafts, humans decide', text: 'Treat suggestions as strong first drafts — always review for facts, names and dates before submitting.' },
    recap: 'AI drafts captions in your saved brand voice with tone control, singly or in weekly bulk.',
    pitch: 'Caption production drops from hours to minutes: on-brand AI drafting with human editorial control at every step.',
    sectionIds: ['ai-writing'],
    images: { mobile: `${CDN}1783415546924_bbe7bd08.png`, desktop: `${CDN}1783415157529_5ecb8626.jpg` },
    imageAlt: 'AI caption assistant with tone chips and three suggested captions',
  },
  {
    id: 'pres-5',
    num: 5,
    kicker: 'AI assistance · Imagery',
    title: 'AI Image Generation',
    lead: 'Describe the scene and the Hub generates publication-ready imagery matched to your visual identity — no stock-photo hunting, no license worries.',
    points: [
      { title: 'Prompt to picture', desc: 'Type what you want to show; the model returns options in seconds.' },
      { title: 'Aspect-ratio presets', desc: 'Square, portrait and story formats sized correctly for each placement.' },
      { title: 'On-brand output', desc: 'Style guidance keeps generated photos consistent with your aesthetic.' },
      { title: 'Straight to the post', desc: 'Attach a generated image to a draft with one click — it lands in the media library too.' },
    ],
    recap: 'Generate on-brand, correctly sized imagery from a text prompt and attach it in one click.',
    pitch: 'Original, rights-clean visuals on demand — eliminating stock subscriptions and photography bottlenecks.',
    sectionIds: ['ai-images'],
    images: { mobile: `${CDN}1783415573124_630e6fc2.png`, desktop: `${CDN}1783415178421_183557ac.jpg` },
    imageAlt: 'AI image generation tool with prompt box and grid of generated travel photos',
  },
  {
    id: 'pres-6',
    num: 6,
    kicker: 'Organizing',
    title: 'Content Library & Media Assets',
    lead: 'Every draft, approved post and media file lives in the searchable library. Status badges tell you at a glance where each item sits in its lifecycle.',
    points: [
      { title: 'One searchable home', desc: 'Filter by status, platform, campaign or keyword to find anything fast.' },
      { title: 'Status badges', desc: 'Draft, Pending, Approved and Scheduled are color-coded on every card.' },
      { title: 'Media library', desc: 'Uploaded and AI-generated assets are stored once and reused everywhere.' },
      { title: 'Never lose work', desc: 'Rejected or expired posts stay archived — recycle strong ideas next season.' },
    ],
    recap: 'The library is the single searchable home for posts and media, with color-coded status badges.',
    pitch: 'Institutional memory built in: every asset and post is stored, searchable and reusable — content investment compounds over time.',
    sectionIds: ['library', 'media'],
    images: { mobile: `${CDN}1783415615134_51b5be22.png`, desktop: `${CDN}1783415197829_a1b6dcdb.jpg` },
    imageAlt: 'Content library grid with travel thumbnails and status badges',
  },
  {
    id: 'pres-7',
    num: 7,
    kicker: 'Quality control',
    title: 'The Approval Workflow',
    lead: 'Nothing reaches a client account without sign-off. Posts move through a clear pipeline — Draft, Pending Review, Approved — with comments attached at every stage.',
    points: [
      { title: 'Three-stage pipeline', desc: 'Draft → Pending Review → Approved. Rejected posts return to Draft with notes.' },
      { title: 'Reviewer comments', desc: 'Feedback lives on the post itself, not in a chat thread somewhere else.' },
      { title: 'Role-gated approval', desc: 'Only Admins and designated reviewers can move a post to Approved.' },
      { title: 'Full audit trail', desc: 'Who approved what, and when, is recorded on every item.' },
    ],
    tip: { title: 'The golden rule', text: 'Only Approved posts can be scheduled or published. If it is not approved, it does not go out. No exceptions.' },
    recap: 'Draft → Pending → Approved with comments and an audit trail; only Approved posts can publish.',
    pitch: 'Brand-safety by design: a mandatory review gate and audit trail make embarrassing mistakes structurally impossible.',
    sectionIds: ['approval'],
    images: { mobile: `${CDN}1783415632340_b5e138f7.jpg`, desktop: `${CDN}1783415239884_4cfd90fe.png` },
    imageAlt: 'Approval board with Draft, Pending Review and Approved columns',
  },
  {
    id: 'pres-8',
    num: 8,
    kicker: 'Timing',
    title: 'The Scheduling System',
    lead: 'The calendar turns approved content into a publishing plan. Drop posts onto time slots, balance post types across the week, or let Auto-Schedule place them at peak engagement times.',
    points: [
      { title: 'Visual weekly calendar', desc: 'See Reels, Carousels and Stories color-coded across the week.' },
      { title: 'Best-time highlights', desc: 'Gold highlights mark the windows your audience is most active.' },
      { title: 'Auto-Schedule', desc: 'One click distributes approved posts into optimal slots automatically.' },
      { title: 'Drag to adjust', desc: 'Plans change — drag any scheduled post to a new slot instantly.' },
    ],
    recap: 'Approved posts are placed on a visual calendar — manually or via one-click Auto-Schedule at peak times.',
    pitch: 'Consistency without headcount: AI-timed scheduling keeps accounts active at peak hours, 7 days a week.',
    sectionIds: ['scheduling'],
    images: { mobile: `${CDN}1783415656246_7c5036b8.jpg`, desktop: `${CDN}1783415260831_752b3a3d.jpg` },
    imageAlt: 'Weekly scheduling calendar with color-coded post chips and auto-schedule button',
  },
  {
    id: 'pres-9',
    num: 9,
    kicker: 'Going live',
    title: 'Publishing',
    lead: 'Publishing connects the Hub to your live social accounts. Preview the post exactly as followers will see it, confirm the connected account, and send it now or at its scheduled time.',
    points: [
      { title: 'True-to-life preview', desc: 'The Instagram-style preview shows precisely what will appear in feed.' },
      { title: 'Connection status', desc: 'A green dot confirms the account link is healthy before you commit.' },
      { title: 'Publish now or later', desc: 'Immediate push, or hands-free publishing at the scheduled slot.' },
      { title: 'Failure handling', desc: 'If a publish fails, the post returns to the queue with an alert — nothing vanishes.' },
    ],
    recap: 'Preview exactly what followers see, confirm the connection, then publish now or on schedule.',
    pitch: 'From approval to live post with zero copy-paste — direct account integration closes the loop end to end.',
    sectionIds: ['publishing'],
    images: { mobile: `${CDN}1783415737225_f521bec9.png`, desktop: `${CDN}1783415301264_40f722c9.jpg` },
    imageAlt: 'Publishing screen with Instagram preview and Publish Now button',
  },
  {
    id: 'pres-10',
    num: 10,
    kicker: 'Measuring',
    title: 'Analytics',
    lead: 'Analytics closes the feedback loop. Engagement trends, reach by post type and best-posting-time insights tell you what to make more of — and what to retire.',
    points: [
      { title: 'Engagement over time', desc: 'The line chart reveals momentum, spikes and slow weeks at a glance.' },
      { title: 'Reach by post type', desc: 'Compare Reels vs Carousels vs Stories to steer the content mix.' },
      { title: 'KPI tiles', desc: 'Followers, impressions and saves tracked against the previous period.' },
      { title: 'Feed the planner', desc: 'Best-time insights flow straight back into Auto-Schedule.' },
    ],
    tip: { title: 'Review weekly', text: 'A weekly analytics review is the single highest-leverage habit — adjust next week\u2019s plan based on what the data says.' },
    recap: 'Charts and KPI tiles show what works; best-time insights feed straight back into scheduling.',
    pitch: 'Decisions, not guesses: built-in analytics turn every post into data that automatically improves the next week\u2019s plan.',
    sectionIds: ['analytics'],
    images: { mobile: `${CDN}1783415755942_a2893b9c.jpg`, desktop: `${CDN}1783415320608_8d0100e4.jpg` },
    imageAlt: 'Analytics dashboard with engagement line chart and KPI tiles',
  },
  {
    id: 'pres-11',
    num: 11,
    kicker: 'Identity',
    title: 'Brand Management',
    lead: 'The Brand Studio stores your visual and verbal identity — logo, palette, typography and voice keywords — and every AI tool in the Hub reads from it.',
    points: [
      { title: 'Single brand source', desc: 'Logo, gold-and-navy palette and type specimens live in one place.' },
      { title: 'Voice profile', desc: 'Keywords like Luxury, Warm and Editorial condition every AI caption.' },
      { title: 'Consistency at scale', desc: 'Ten people can create content that reads and looks like one author.' },
      { title: 'Update once', desc: 'Change the profile and all future AI output shifts with it instantly.' },
    ],
    recap: 'Brand Studio holds logo, palette and voice; every AI tool reads from it for on-brand output.',
    pitch: 'Brand consistency as infrastructure: one saved identity governs every caption and image the platform produces.',
    sectionIds: ['brand'],
    images: { mobile: `${CDN}1783415781157_9e9addf8.png`, desktop: `${CDN}1783415338693_70035b07.jpg` },
    imageAlt: 'Brand studio with color swatches, typography and voice keywords',
  },
  {
    id: 'pres-12',
    num: 12,
    kicker: 'Scale',
    title: 'Campaigns & Automation',
    lead: 'Group related posts into campaigns with shared timelines, then let automation rules handle the repetitive work — scheduling, captioning and weekly content batches.',
    points: [
      { title: 'Campaign timelines', desc: 'Launches, seasons and promotions tracked with milestone markers.' },
      { title: 'Automation toggles', desc: 'Auto-Schedule, Auto-Caption and Weekly Batch run while you sleep.' },
      { title: 'Human checkpoints', desc: 'Automated output still enters the approval pipeline — always.' },
      { title: 'Set and refine', desc: 'Rules are toggles, not code: switch them on, watch, adjust.' },
    ],
    recap: 'Campaigns group related posts; automation rules do repetitive work but never skip approval.',
    pitch: 'Managed automation multiplies output per team member while the approval gate keeps humans firmly in control.',
    sectionIds: ['campaigns', 'automation'],
    images: { mobile: `${CDN}1783415800221_d3542d03.png`, desktop: `${CDN}1783415360479_190deb08.png` },
    imageAlt: 'Campaign timeline with automation rule toggles',
  },
  {
    id: 'pres-13',
    num: 13,
    kicker: 'People & preferences',
    title: 'Team Collaboration & Settings',
    lead: 'Roles keep responsibilities clear: Admins configure and approve, Editors create, Viewers observe. Settings tune notifications, security and account connections per workspace.',
    points: [
      { title: 'Three roles', desc: 'Admin, Editor and Viewer — permissions match responsibility.' },
      { title: 'Invite in seconds', desc: 'Add a teammate by email and assign their role on the spot.' },
      { title: 'Notification control', desc: 'Choose which events ping you: approvals, publishes, failures.' },
      { title: 'Security settings', desc: 'Session, password and connected-account controls live under Settings.' },
    ],
    recap: 'Admin / Editor / Viewer roles divide responsibility; Settings governs notifications and security.',
    pitch: 'Enterprise-grade access control out of the box — clients, managers and creators each see exactly what they should.',
    sectionIds: ['collaboration', 'settings'],
    images: { mobile: `${CDN}1783415825398_4c1709a7.png`, desktop: `${CDN}1783415387477_e91ed501.jpg` },
    imageAlt: 'Team member list with role badges and settings toggles',
  },
  {
    id: 'pres-14',
    num: 14,
    kicker: 'Working like a pro',
    title: 'Tips & Best Practices',
    lead: 'The Hub rewards rhythm. High-performing teams batch their creation, schedule a week ahead, post at golden hours and let data — not habit — steer the mix.',
    points: [
      { title: 'Batch creation', desc: 'Write a week of content in one sitting; quality and voice stay consistent.' },
      { title: 'Schedule ahead', desc: 'A seven-day runway absorbs surprises without breaking cadence.' },
      { title: 'Golden-hour posting', desc: 'Lean on best-time highlights instead of posting on impulse.' },
      { title: 'Hook in line one', desc: 'The first caption line decides the tap — front-load the payoff.' },
    ],
    tip: { title: 'Cadence beats bursts', text: 'Four good posts a week, every week, outperforms twelve posts one week and silence the next.' },
    recap: 'Batch, schedule a week ahead, post at golden hours, hook early — cadence beats bursts.',
    pitch: 'The platform encodes professional social strategy into the workflow itself, so best practice is the default, not a memo.',
    sectionIds: ['best-practices'],
    images: { mobile: `${CDN}1783415846058_04993f49.jpg`, desktop: `${CDN}1783415409233_6f1f2840.jpg` },
    imageAlt: 'Best practices checklist with progress ring',
  },
  {
    id: 'pres-15',
    num: 15,
    kicker: 'Support & self-rescue',
    title: 'FAQ, Troubleshooting & Wrap-Up',
    lead: 'When something looks wrong, the help center is your first stop: searchable FAQs, status indicators and a proven troubleshooting order that solves most issues in minutes.',
    points: [
      { title: 'Search first', desc: 'Most questions are already answered in the FAQ accordion.' },
      { title: 'Check status', desc: 'Green and amber indicators reveal connection or service issues instantly.' },
      { title: 'Troubleshoot in order', desc: 'Refresh → check connection → re-authenticate account → contact support.' },
      { title: 'You\u2019ve seen it all', desc: 'Dashboard to publishing to analytics — you now know every corner of the Hub.' },
    ],
    recap: 'FAQ + status indicators + the four-step troubleshooting order resolve most issues without support.',
    pitch: 'Self-service support and transparent status reduce downtime and support cost — the platform explains itself.',
    sectionIds: ['faq', 'troubleshooting', 'summary'],
    images: { mobile: `${CDN}1783415892856_1d5f597a.png`, desktop: `${CDN}1783415431765_b5634402.png` },
    imageAlt: 'Help center with FAQ accordion and status indicators',
  },
];

/** Review-slide "big ideas" — the course in five lines. */
export const REVIEW_BIG_IDEAS: string[] = [
  'Everything lives in one hub: create, approve, schedule, publish, measure.',
  'AI drafts words and images in your brand voice — humans always review.',
  'Only Approved content can be scheduled or published. No exceptions.',
  'Auto-Schedule + analytics form a loop that gets smarter every week.',
  'Roles, the audit trail and the help center keep the whole team safe.',
];

/** Presentation steps = 15 content slides + 1 review slide. */
export const PRESENTATION_STEPS = PRESENTATION_SLIDES.length + 1;
export const REVIEW_INDEX = PRESENTATION_SLIDES.length; // 0-based index of review

/** Course card metadata. */
export const COURSE_META = {
  title: 'Social Media Hub Mastery',
  subtitle: `All ${COURSE_SECTIONS.length} Academy sections condensed into a guided ${PRESENTATION_SLIDES.length}-slide presentation with real product screenshots — mobile and desktop — plus a review slide and the certification assessment.`,
  minutes: TOTAL_MINUTES,
  cover: `${CDN}1783415090241_21bcb035.jpg`,
};

/** Map original section id -> presentation slide index (for quiz review links). */
export const SECTION_TO_SLIDE: Record<string, number> = PRESENTATION_SLIDES.reduce(
  (acc, s, idx) => {
    s.sectionIds.forEach((sid) => {
      acc[sid] = idx;
    });
    return acc;
  },
  {} as Record<string, number>,
);

/** All slide ids in order (progress tracking). */
export const PRESENTATION_SLIDE_IDS: string[] = [
  ...PRESENTATION_SLIDES.map((s) => s.id),
  'pres-review',
];
